import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Download, Check, MapPin, Users, Calendar, MessageCircle, Phone } from "lucide-react";
import { fehlerText } from "@/lib/fehler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EUR = (v) => `${(Number(v) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const Ornament = ({ className = "" }) => (
  <div className={`flex items-center justify-center gap-3 ${className}`}>
    <span className="w-16 h-px bg-[#BA6D52]/50" />
    <span className="w-2 h-2 rotate-45 border border-[#BA6D52]" />
    <span className="w-16 h-px bg-[#BA6D52]/50" />
  </div>
);

export default function RechnungSeite() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/rechnungsseiten/${slug}`);
        setData(d);
      } catch (err) {
        setError(fehlerText(err, "Diese Rechnung gibt es nicht — bitte den Link prüfen."));
      }
    })();
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDF8F1] flex items-center justify-center px-6">
        <p className="font-serif text-2xl text-[#2B2320] text-center" data-testid="rechnung-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#FDF8F1] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#BA6D52]" size={30} />
      </div>
    );
  }

  const s = data.summen || {};
  const privat = data.kundentyp === "privat";
  const istTagessatz = (data.art || "menue") === "tagessatz";

  return (
    <div data-testid="page-rechnung" className="min-h-screen bg-[#FDF8F1] text-[#2B2320]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/event-bg-teller.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(20,18,14,0.76)" }} />
        <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-b from-transparent to-[#FDF8F1]" />
        <div className="relative max-w-3xl mx-auto px-6 pt-10 pb-24 text-center">
          <img src="/logo.png" alt="Lukas Stutz"
            className="h-16 w-16 md:h-20 md:w-20 mx-auto rounded-full bg-[#FAF8F3]/90 p-1.5" />
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#E2C4B2] mt-4">
            Lukas Stutz · Mietkoch für Gastronomie &amp; Events
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#FFFBF4] mt-10 drop-shadow-[0_3px_16px_rgba(0,0,0,0.9)]"
            data-testid="rechnung-anrede">
            Vielen Dank{data.anrede ? `, ${data.anrede}` : ""}
          </h1>
          <p className="text-base md:text-lg text-[#F2E7DA] mt-6 font-light leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]">
            {data.anlass ? `Ihr ${data.anlass} ist gut über die Bühne gegangen — ` : ""}
            hier Ihre Rechnung zum Einsatz. Sie können sie direkt als PDF herunterladen.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 mt-10 text-sm text-[#EADFD2]">
            {data.leistungsdatum && (
              <span className="inline-flex items-center gap-2" data-testid="rechnung-leistungsdatum">
                <Calendar size={14} className="text-[#D99A5B]" /> {data.leistungsdatum_lang}
              </span>
            )}
            {data.ort && <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-[#D99A5B]" />{data.ort}</span>}
            {s.personen > 0 && (
              <span className="inline-flex items-center gap-2" data-testid="rechnung-personen">
                <Users size={14} className="text-[#D99A5B]" />
                {istTagessatz ? `${s.personen} Einsatztag${s.personen !== 1 ? "e" : ""}` : `${s.personen} Personen`}
              </span>
            )}
          </div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A392] mt-8" data-testid="rechnung-nummer">
            Rechnung {data.nummer}{data.rechnung_datum_de ? ` · ${data.rechnung_datum_de}` : ""}
          </p>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-5 sm:px-6 pb-4 -mt-14 relative">
        <div className="bg-[#FFFDF8] border border-[#E6D8C2] shadow-[0_26px_60px_-38px_rgba(120,80,48,0.55)] px-6 sm:px-10 py-10">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#BA6D52]">Ihre Rechnung</p>
          <h2 className="font-serif text-3xl font-light mt-3" data-testid="rechnung-titel">
            {data.nummer}
          </h2>

          <div className="mt-8 space-y-3 text-sm" data-testid="rechnung-positionen">
            <div className="flex justify-between gap-4 pb-3 border-b border-[#EBDFCB]">
              <span className="text-[#5A4D43]">
                {istTagessatz ? "Mietkoch — Tagessatz" : "Menü"}{data.anlass ? ` — ${data.anlass}` : ""}
                <span className="block text-xs text-[#5E5147] mt-1">
                  {istTagessatz
                    ? `${s.personen} Einsatztag${s.personen !== 1 ? "e" : ""} × `
                    : `${s.personen} Personen × `}{`${EUR(s.pro_person_netto)} netto`}
                  {privat ? ` (${EUR(s.pro_person_brutto)} inkl. MwSt)` : ""}
                </span>
              </span>
              <span className="tabular-nums" data-testid="rechnung-netto">{EUR(s.positionen_netto ?? s.netto)}</span>
            </div>
            {s.anfahrt_netto > 0 && (
              <div className="flex justify-between gap-4 pb-3 border-b border-[#EBDFCB]" data-testid="rechnung-anfahrt">
                <span className="text-[#5A4D43]">
                  Anfahrtspauschale
                  {s.anfahrt_km > 0 && (
                    <span className="block text-xs text-[#5E5147] mt-1">ca. {Math.round(s.anfahrt_km)} km</span>
                  )}
                </span>
                <span className="tabular-nums">{EUR(s.anfahrt_netto)}</span>
              </div>
            )}
            <div className="flex justify-between gap-4">
              <span className="text-[#5E5147]">Summe netto</span>
              <span className="tabular-nums">{EUR(s.netto)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-[#5E5147]">MwSt {s.mwst_prozent} %</span>
              <span className="tabular-nums" data-testid="rechnung-mwst">{EUR(s.mwst_betrag)}</span>
            </div>
            <div className="flex justify-between gap-4 pt-4 border-t border-[#D9C7AE] font-medium text-base">
              <span>Gesamtbetrag</span>
              <span className="tabular-nums" data-testid="rechnung-brutto">{EUR(s.brutto)}</span>
            </div>
          </div>

          {data.bezahlt ? (
            <p className="mt-7 inline-flex items-center gap-2 text-sm text-[#4F6B3E] border border-[#CFE0C3] bg-[#F3F7EF] px-4 py-2.5"
              data-testid="rechnung-bezahlt">
              <Check size={15} /> Bezahlt — vielen Dank.
            </p>
          ) : (
            <div className="mt-7 border border-[#EBDFCB] bg-[#FAF5F1] px-5 py-5 text-sm" data-testid="rechnung-zahlung">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Zahlung</p>
              {data.faellig_am_de && (
                <p className="mt-3 text-[#5A4D43]">Zahlbar bis <b>{data.faellig_am_de}</b></p>
              )}
              {data.iban && (
                <p className="mt-2 text-[#5A4D43]">
                  IBAN <span className="font-mono tracking-wide">{data.iban}</span>
                  {data.bank ? ` · ${data.bank}` : ""}
                </p>
              )}
              <p className="mt-2 text-[#5A4D43]">Verwendungszweck <b>{data.verwendungszweck}</b></p>
            </div>
          )}

          <a href={`${API}/rechnungsseiten/${slug}/pdf`} data-testid="rechnung-download"
            className="mt-8 w-full inline-flex items-center justify-center gap-2.5 bg-[#BA6D52] hover:bg-[#9C4F31] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors">
            <Download size={15} /> Rechnung als PDF herunterladen
          </a>

          {data.hinweis && (
            <p className="text-sm text-[#4A3F36] mt-7 font-light leading-relaxed whitespace-pre-line"
              data-testid="rechnung-hinweis">
              {data.hinweis}
            </p>
          )}
          <p className="text-[11px] text-[#6E5B4C] mt-7">
            {data.auftrag_nummer ? `Zur Auftragsbestätigung ${data.auftrag_nummer}` : ""}
          </p>
        </div>
      </section>

      {(data.gaenge || []).length > 0 && (
        <section className="max-w-2xl mx-auto px-5 sm:px-6 py-14" data-testid="rechnung-menu">
          <div className="text-center">
            <Ornament />
            <h2 className="font-serif text-3xl font-light mt-5">Das haben wir serviert</h2>
          </div>
          <div className="mt-10 space-y-7">
            {data.gaenge.map((g, i) => (
              <div key={i} className="text-center" data-testid={`rechnung-gang-${i}`}>
                {g.label && <p className="text-[9px] uppercase tracking-[0.3em] text-[#BA6D52]">{g.label}</p>}
                <p className="font-serif text-xl mt-2 leading-snug">{g.titel}</p>
                {g.beschreibung && (
                  <p className="text-sm text-[#4A3F36] mt-1.5 font-light italic">{g.beschreibung}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="relative overflow-hidden text-[#FAF3E8]">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/event-bg-gericht.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(14,13,10,0.72)" }} />
        <div className="relative max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-light">Eine Frage zur Rechnung?</h2>
          <p className="text-sm text-[#A89D91] mt-3 font-light">
            Schreiben Sie kurz oder rufen Sie an — ich kümmere mich sofort darum.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <a href={`https://wa.me/${data.phone_intl}?text=${encodeURIComponent(`Guten Tag Herr Stutz, eine Frage zur Rechnung ${data.nummer}:`)}`}
              target="_blank" rel="noreferrer" data-testid="rechnung-whatsapp"
              className="inline-flex items-center gap-2 border border-emerald-700/60 text-emerald-300 hover:bg-emerald-900/20 px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors">
              <MessageCircle size={15} /> WhatsApp
            </a>
            <a href={`tel:+${data.phone_intl}`} data-testid="rechnung-call"
              className="inline-flex items-center gap-2 border border-[#4A433B] text-[#FAF3E8] hover:border-[#BA6D52] px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors">
              <Phone size={15} /> Anrufen
            </a>
          </div>
          <p className="text-[11px] text-[#6E655C] mt-12">
            Lukas Stutz · Mietkoch für Gastronomie &amp; Events · stutz-cuisine.com
          </p>
        </div>
      </section>
    </div>
  );
}
